import { createContext, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { userAPI } from '../API/userAPI';
import { loginRequest } from '../Utils/APIutils/msalConfig';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const mapUser = (src) => ({
  id: src.id ?? null,
  name: src.name ?? '',
  email: src.email ?? '',
  jobTitle: src.jobTitle ?? '',
  avatar: src.photo ? `data:image/jpeg;base64, ${src.photo}` : null,
  role: src.role ?? 'USER',
  clientId: src.clientId ?? null,
  storeIds: src.storeIds ?? [],
  roles: [src.role ?? 'USER'],
  permissions: src.permissions ?? [],
  defaultRoute: src.defaultRoute ?? '/Gateway/Dashboard',
  userModules: src.userModules ?? [],
});

// ─── State ────────────────────────────────────────────────────────────────────

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => ({
    ...state,
    isAuthenticated: action.payload.isAuthenticated,
    isInitialized: true,
    user: action.payload.user,
  }),
  LOGIN: (state, action) => ({
    ...state,
    isAuthenticated: true,
    user: action.payload.user,
  }),
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) =>
  (handlers[action.type] ? handlers[action.type](state, action) : state);

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext({
  ...initialState,
  platform: 'JWT',
  login: () => Promise.resolve(),
  loginWithEmail: () => Promise.resolve(),
  loginWithMicrosoft: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children, instance }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const instanceRef = useRef(instance);

  useEffect(() => {
    const initialize = async () => {
      try {
        // ── NONE: bypass all auth (local dev) ──────────────────────────────
        if (process.env.REACT_APP_CUSTOM_AUTH === 'NONE') {
          // If a real user session was stored (e.g. logged in via email to test a specific
          // role), honour it so a page refresh doesn't silently replace them with the dev user.
          const existingRaw = window.localStorage.getItem('session');
          const existingSession = existingRaw ? JSON.parse(existingRaw) : null;
          if (existingSession?.name && existingSession?.accessToken) {
            dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: true, user: mapUser(existingSession) } });
            return;
          }

          // No real session — try to get a dev JWT from the backend so authenticated
          // endpoints (e.g. api/User) work without credential prompts.
          try {
            const devData = await userAPI.devToken();
            if (devData && !devData.error && devData.token) {
              localStorage.setItem('session', JSON.stringify({ ...devData, accessToken: devData.token }));
              dispatch({
                type: 'INITIALIZE',
                payload: { isAuthenticated: true, user: mapUser(devData) },
              });
              return;
            }
          } catch (_) {
            // DevToken endpoint not available — fall through to unauthenticated bypass
          }
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: {
                id: 'dev-user',
                name: 'Dev User',
                email: 'dev@gateway.pt',
                jobTitle: 'Developer',
                avatar: null,
                role: 'GATEWAY_ADMIN',
                clientId: null,
                storeIds: [],
                roles: [],
                permissions: [
                  'CLIENT_READ', 'CLIENT_WRITE',
                  'STORE_READ', 'STORE_WRITE',
                  'DEVICE_READ', 'DEVICE_WRITE',
                  'USER_READ', 'USER_WRITE',
                  'PROFILE_READ', 'PROFILE_WRITE',
                  'ALERT_READ', 'ALERT_WRITE',
                  'ANALYTICS_READ',
                ],
                defaultRoute: '/Gateway/Dashboard',
                userModules: [],
              },
            },
          });
          return;
        }

        const raw = window.localStorage.getItem('session');
        const session = raw ? JSON.parse(raw) : null;

        // ── AZUREAD: use MSAL to get a fresh Azure AD token, exchange for local JWT ──
        if (process.env.REACT_APP_CUSTOM_AUTH === 'AZUREAD') {
          const msal = instanceRef.current;
          const accounts = msal ? msal.getAllAccounts() : [];

          if (accounts.length === 0) {
            // No MSAL account — user may have logged in with email/password (local JWT)
            if (session?.name && session?.accessToken) {
              dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: true, user: mapUser(session) } });
            } else {
              localStorage.removeItem('session');
              dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: false, user: null } });
            }
            return;
          }

          try {
            const result = await msal.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
            const azureToken = result.accessToken;

            // Temporarily store the Azure AD token so handleMethod sends it to AzureAuthenticate.
            // Preserve the existing session so we can restore it if the backend call fails.
            localStorage.setItem('session', JSON.stringify({ accessToken: azureToken }));

            const data = await userAPI.AzureAuthenticate();

            if (data && !data.error && data.name && data.token) {
              // Exchange successful — store the local JWT issued by the backend
              localStorage.setItem('session', JSON.stringify({ ...data, accessToken: data.token }));
              dispatch({
                type: 'INITIALIZE',
                payload: { isAuthenticated: true, user: mapUser(data) },
              });
              return;
            }

            // AzureAuthenticate returned an error (backend/DB issue).
            // If we had a valid local JWT before, restore it and keep the user logged in.
            // The JWT itself will be validated on the next API call; a 401 will force re-login.
            if (session?.name && session?.accessToken) {
              if (raw) localStorage.setItem('session', raw);
              dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: true, user: mapUser(session) } });
              return;
            }
          } catch (silentErr) {
            console.warn('MSAL silent renewal or AzureAuthenticate failed:', silentErr?.message);
            // MSAL token refresh failed but we still have a valid local JWT — keep the user in.
            if (session?.name && session?.accessToken) {
              if (raw) localStorage.setItem('session', raw);
              dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: true, user: mapUser(session) } });
              return;
            }
          }

          // Could not get a valid token — require fresh login
          localStorage.removeItem('session');
          dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: false, user: null } });
          return;
        }

        // ── LDAP / any other method: restore from cached session ────────────
        // The stored accessToken is a local JWT; 401 responses handle expiry.
        if (session?.name && session?.accessToken) {
          dispatch({
            type: 'INITIALIZE',
            payload: { isAuthenticated: true, user: mapUser(session) },
          });
        } else {
          dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: false, user: null } });
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: false, user: null } });
      }
    };

    initialize();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Login with email + password (local SQL Server user) ─────────────────────
  const loginWithEmail = async (email, password) => {
    const data = await userAPI.Authenticate(email, password);
    if (!data.error && data.token) {
      const session = { ...data, accessToken: data.token, isEntraId: false };
      localStorage.setItem('session', JSON.stringify(session));
      dispatch({ type: 'LOGIN', payload: { user: mapUser(data) } });
    } else {
      throw new Error(data.description ?? 'Utilizador ou password incorrectos.');
    }
  };

  // ── Login with Microsoft (Entra ID) ─────────────────────────────────────────
  // Flow: MSAL loginPopup → Azure AD token → AzureAuthenticate → local JWT
  // The Azure AD token is used ONLY for the one-time exchange; the local JWT is
  // stored in localStorage and used for all subsequent API calls (same as LDAP).
  const loginWithMicrosoft = async (instance) => {
    // Get Azure AD access token. Try API scope first; fall back to basic OIDC
    // scopes if "Expose an API / access_as_user" is not yet configured.
    let msalResponse;
    try {
      msalResponse = await instance.loginPopup(loginRequest);
    } catch (scopeErr) {
      if (scopeErr?.errorCode === 'invalid_resource' || scopeErr?.message?.includes('AADSTS')) {
        msalResponse = await instance.loginPopup({ scopes: ['openid', 'profile', 'email'] });
      } else {
        throw scopeErr;
      }
    }

    const azureToken = msalResponse.accessToken;

    // Temporarily store the Azure AD token so handleMethod can send it
    localStorage.setItem('session', JSON.stringify({ accessToken: azureToken, isEntraId: true }));

    // Exchange Azure AD token for a backend-issued local JWT with roles
    const data = await userAPI.AzureAuthenticate();

    if (!data || data.error || !data.name || !data.token) {
      localStorage.removeItem('session');
      throw new Error('Erro ao autenticar com Microsoft. Verifique se o servidor está disponível.');
    }

    // Store the local JWT — from here on, all API calls behave like LDAP users
    const session = { ...data, accessToken: data.token, isEntraId: true };
    localStorage.setItem('session', JSON.stringify(session));
    dispatch({ type: 'LOGIN', payload: { user: mapUser(data) } });
  };

  // ── Logout ──────────────────────────────────────────────────────────────────
  const logout = async () => {
    let isEntraId = false;
    try {
      const rawSession = localStorage.getItem('session');
      if (rawSession) {
        const parsed = JSON.parse(rawSession);
        isEntraId = parsed.isEntraId === true || parsed.authProvider === 'ENTRA_ID';
      }
    } catch (e) {
      // Ignore session parse errors on logout
    }

    localStorage.removeItem('session');
    const msal = instanceRef.current;
    if (isEntraId && msal && msal.getAllAccounts().length > 0) {
      try {
        const activeAccount = msal.getActiveAccount() || msal.getAllAccounts()[0];
        await msal.logoutPopup({
          account: activeAccount,
          mainWindowRedirectUri: window.location.origin,
        });
      } catch (err) {
        console.warn('MSAL logoutPopup warning:', err);
      }
    } else if (msal) {
      try {
        msal.setActiveAccount(null);
      } catch (e) {
        // Ignore msal reset errors on logout
      }
    }
    dispatch({ type: 'LOGOUT' });
  };

  const login = loginWithEmail;

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        loginWithEmail,
        loginWithMicrosoft,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  instance: PropTypes.any,
};

export default AuthContext;
